/**
 * AI overlay panel — right-docked tab for pathology AI layers.
 *
 * Modelled on Aperio HALO AP's AI panel:
 *   - "Run <algorithm>" button that turns into a progress bar while
 *     the model runs, then resolves into a layer with a result chip.
 *   - Layer list: toggle visibility, per-layer opacity slider (0-100 %),
 *     result count + percentage chips.
 *   - Legend colours double as the overlay tints in the viewport.
 *
 * The actual model call is stubbed today — we synthesise a plausible
 * Ki67 result in ~800 ms so the whole loop (button → progress → result
 * → report auto-fill) is exercisable end-to-end against the existing
 * pathology-simulated study.
 */
import { createSignal, For, Show } from "solid-js";
import type { AIOverlayLayer } from "./WSIViewport";
import type { DecodedPixels } from "@viewer/core/dicom";

export interface Ki67Result {
  /** Ki67 proliferation index = positive / (positive + negative) × 100 */
  indexPct: number;
  positive: number;
  negative: number;
  stromal:  number;
  ranAt:    number; // unix ms
  model:    string;
}

export interface AIOverlayPanelProps {
  pixels:     DecodedPixels | null;
  layers:     AIOverlayLayer[];
  setLayers:  (next: AIOverlayLayer[]) => void;
  onKi67:     (result: Ki67Result) => void;
}

const STATE_IDLE     = "idle";
const STATE_RUNNING  = "running";
const STATE_DONE     = "done";

export function AIOverlayPanel(p: AIOverlayPanelProps) {
  const [algState, setAlgState] = createSignal<"idle" | "running" | "done">(STATE_IDLE);
  const [progress, setProgress] = createSignal(0);
  const [result,   setResult]   = createSignal<Ki67Result | null>(null);

  function setLayer(id: string, patch: Partial<AIOverlayLayer>) {
    p.setLayers(p.layers.map((l) => l.id === id ? { ...l, ...patch } : l));
  }

  async function runKi67() {
    if (!p.pixels) return;
    setAlgState(STATE_RUNNING);
    setProgress(0);
    // --- Simulated progress ----------------------------------------
    // A real model would stream progress via SSE or long-poll; we
    // tick a signal every 80 ms to exercise the UI path.
    const ticks = 10;
    for (let i = 0; i < ticks; i++) {
      await new Promise((r) => setTimeout(r, 80));
      setProgress((i + 1) / ticks);
    }

    // --- Synthetic result ------------------------------------------
    // Produce plausible Ki67 tallies from deterministic pseudo-random
    // counts keyed on the study's width so successive "runs" return
    // the same numbers (demo repeatability).
    const seed = (p.pixels.width + p.pixels.height) & 0xffff;
    const rnd = (n: number) => Math.abs((Math.sin(seed + n) * 1e6) % 1);
    const positive = Math.round(800 + rnd(1) * 400);
    const negative = Math.round(2400 + rnd(2) * 800);
    const stromal  = Math.round(1500 + rnd(3) * 600);
    const res: Ki67Result = {
      indexPct: (positive / (positive + negative)) * 100,
      positive, negative, stromal,
      ranAt: Date.now(),
      model: "vpd-ki67-sim-v0",
    };

    // --- Overlay layers --------------------------------------------
    // Synthesise three layers at native slide resolution: positive
    // nuclei, negative nuclei, and stromal background. Each is a
    // stipple pattern so the tissue underneath remains readable.
    const W = Math.min(p.pixels.width,  512);
    const H = Math.min(p.pixels.height, 512);
    const mk = (r: number, g: number, b: number, density: number): ImageData => {
      const img = new ImageData(W, H);
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (Math.random() < density) {
            const j = (y * W + x) * 4;
            img.data[j]     = r;
            img.data[j + 1] = g;
            img.data[j + 2] = b;
            img.data[j + 3] = 200;
          }
        }
      }
      return img;
    };
    const layers: AIOverlayLayer[] = [
      { id: "ki67-pos", label: `Ki67+ (${res.positive})`,
        color: "#f85149", opacity: 1, visible: true,
        mask: mk(248, 81, 73, 0.012) },
      { id: "ki67-neg", label: `Ki67− (${res.negative})`,
        color: "#1f6feb", opacity: 1, visible: true,
        mask: mk(31, 111, 235, 0.012) },
      { id: "stroma",   label: `Stroma (${res.stromal})`,
        color: "#8b949e", opacity: 0.6, visible: false,
        mask: mk(139, 148, 158, 0.010) },
    ];
    p.setLayers(layers);
    setResult(res);
    setAlgState(STATE_DONE);
    p.onKi67(res);
  }

  function reset() {
    setAlgState(STATE_IDLE);
    setResult(null);
    setProgress(0);
    p.setLayers([]);
  }

  return (
    <div class="flex h-full flex-col text-[11px]">
      {/* ------------- Algorithm runner ------------- */}
      <div class="border-b border-ink-300 p-3">
        <div class="mb-2 flex items-center gap-2">
          <span class="chip-brand !text-[9px]">Ki67 AI</span>
          <span class="text-ink-700">Breast · IHC</span>
        </div>
        <Show when={algState() === STATE_IDLE}>
          <button type="button"
                  class="btn-brand w-full text-xs"
                  disabled={!p.pixels}
                  onClick={runKi67}>
            ▶ Run Ki67
          </button>
          <div class="mt-2 text-[10px] text-ink-700">
            Positive / negative nuclei + stromal background overlay.
            Draft Ki67 proliferation index auto-fills the Report.
          </div>
        </Show>
        <Show when={algState() === STATE_RUNNING}>
          <div class="rounded border border-ink-300 bg-ink-200 p-2">
            <div class="mb-1 flex items-center justify-between text-ink-700">
              <span>scoring tissue…</span>
              <span class="font-mono">{Math.round(progress() * 100)}%</span>
            </div>
            <div class="h-1.5 w-full overflow-hidden rounded bg-ink-100">
              <div class="h-full bg-brand transition-all"
                   style={{ width: `${Math.round(progress() * 100)}%` }} />
            </div>
          </div>
        </Show>
        <Show when={algState() === STATE_DONE && result()}>
          <div class="rounded border border-brand/50 bg-brand/10 p-2">
            <div class="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-700">
              <span>Ki67 index</span>
              <span class="text-ink-700">{result()!.model}</span>
            </div>
            <div class="font-mono text-2xl text-ink-1000">
              {result()!.indexPct.toFixed(1)}%
            </div>
            <div class="mt-1 flex gap-2 text-[10px]">
              <span><span class="inline-block h-2 w-2 rounded-sm align-middle" style="background:#f85149" /> + {result()!.positive}</span>
              <span><span class="inline-block h-2 w-2 rounded-sm align-middle" style="background:#1f6feb" /> − {result()!.negative}</span>
              <span><span class="inline-block h-2 w-2 rounded-sm align-middle" style="background:#8b949e" /> stroma {result()!.stromal}</span>
            </div>
            <button type="button" class="btn-ghost mt-2 w-full !text-[10px]" onClick={reset}>
              clear overlay
            </button>
          </div>
        </Show>
      </div>

      {/* ------------- Layer list ------------- */}
      <Show when={p.layers.length > 0}>
        <div class="flex-1 overflow-auto">
          <div class="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-700">
            overlay layers
          </div>
          <For each={p.layers}>{(l) => (
            <div class="border-t border-ink-300 px-3 py-2">
              <div class="mb-1 flex items-center gap-2">
                <input type="checkbox"
                       checked={l.visible}
                       class="h-3.5 w-3.5 accent-brand"
                       onChange={(e) => setLayer(l.id, {
                         visible: (e.currentTarget as HTMLInputElement).checked,
                       })} />
                <span class="h-2.5 w-2.5 rounded-sm" style={{ "background-color": l.color }} />
                <span class="truncate text-ink-900">{l.label}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-[9px] uppercase text-ink-700">opacity</span>
                <input type="range" min="0" max="1" step="0.01"
                       value={l.opacity}
                       class="h-1 flex-1 accent-brand"
                       onInput={(e) => setLayer(l.id, {
                         opacity: parseFloat((e.currentTarget as HTMLInputElement).value),
                       })} />
                <span class="font-mono text-[10px] text-ink-700 w-8 text-right">
                  {Math.round(l.opacity * 100)}%
                </span>
              </div>
            </div>
          )}</For>
        </div>
      </Show>

      {/* ------------- Help footer ------------- */}
      <div class="border-t border-ink-300 p-3 text-[10px] leading-snug text-ink-700">
        Simulated model (<code>vpd-ki67-sim-v0</code>) — produces a
        deterministic score keyed on slide dimensions so demos
        repeat. Swap for a real AI service by redirecting{" "}
        <code>runKi67()</code> at <code>/viewer-api/ai/path/ki67</code>.
      </div>
    </div>
  );
}
